import { useEffect, useMemo, useRef, useState } from 'react'
import {
  setConnectionCredential,
  createResourceConnection,
  fetchHostPubkey,
  fetchPluginInstalls,
  fetchResourceConnections,
  fetchSecrets,
  putSecret,
  wsRequest,
} from '../../api/ws'
import { getSetupGuideURL } from '../../api/config'
import { base64Decode, base64Encode, encryptToHostPubkey } from '../../lib/secretEnvelope'
import { useAppStore } from '../../store/app'
import type { Host, PluginInstall } from '../../store/types'
import { foreignHostWarning } from './foreignHostWarning'
import '../agents/NewAgentWizard.css'
import {
  AuthSchemeBadge,
  authSchemeExplainer,
  buildConfigJSON,
  ConfigStep,
  CredentialStep,
  DetailsStep,
  OAuthConnectStep,
  oauthProviderFromSpecs,
  parseConfigSchema,
  parseCredentialSpecs,
  slugify,
} from './wizardParts'

// ConnectAccountWizard is the household-level "connect an account" flow —
// Surface A of the resource-connection UX (docs/resource-connection-ux-plan.md
// §3). It takes a homeowner from "I want to connect Google Calendar" to a
// live resource_connection with its credential filed, and NO agent involved.
// Granting an agent access to it is a separate, later step (the agent's
// Resources tab, or Surface B once it lands).
//
//   pick service → [pick host] → name → [config] → authenticate → done
//
// The host step is skipped unless the chosen service is installed on more
// than one host — normally the service pick implies the host. Authenticate
// branches on auth_scheme: oauth2_user runs the OAuth dance; everything
// else collects a credential and stores it as a secret. Either way the
// result is one identity the household owns.

type Step = 'service' | 'host' | 'details' | 'config' | 'cred' | 'auth' | 'done'

// A service groups a plugin_slug's installs across hosts, so the picker
// shows "Google Calendar" once even when two hosts have it.
type Service = {
  slug: string
  displayName: string
  authScheme?: string
  installs: PluginInstall[]
}

export function ConnectAccountWizard() {
  const popSheet = useAppStore((s) => s.popSheet)
  const pushNav = useAppStore((s) => s.pushNav)
  const organizationID = useAppStore((s) => s.organizationID)
  const hosts = useAppStore((s) => s.hosts)
  const pluginInstalls = useAppStore((s) => s.pluginInstalls)
  const allSecrets = useAppStore((s) => s.secrets)
  const humanUserID = useAppStore((s) => s.humanUserID)

  useEffect(() => {
    fetchPluginInstalls()
    fetchSecrets()
  }, [])

  const [step, setStep] = useState<Step>('service')
  const [pickedServiceSlug, setPickedServiceSlug] = useState<string>('')
  const [pickedInstallID, setPickedInstallID] = useState<string>('')
  const [connectionSlug, setConnectionSlug] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [configValues, setConfigValues] = useState<Record<string, string>>({})
  const [credValues, setCredValues] = useState<Map<number, string>>(new Map())
  const [credSecretIDs, setCredSecretIDs] = useState<Map<number, string>>(new Map())
  const [credIndex, setCredIndex] = useState(0)

  // Created lazily the first time a step needs it (OAuth needs the
  // connection to exist before begin_oauth; the credential path creates it
  // at submit). Reused on retry so a failed OAuth round doesn't spawn a
  // second orphan connection — the exact junk-connection trap that made
  // the old flow unusable.
  const [connID, setConnID] = useState<string | null>(null)

  // Resource→jobs sweep (onboarding.md §5, decision D): once the connection
  // exists (the wizard reached 'done'), propose bindings to the household's
  // existing agents whose role could use it, and surface the review inline.
  // Best-effort — no suggestions (or a hiccup) just leaves the normal done
  // screen; it never blocks completing the connection.
  const sweptRef = useRef(false)
  useEffect(() => {
    if (step !== 'done' || !connID || !organizationID || sweptRef.current) return
    sweptRef.current = true
    void wsRequest<{
      provisioning_proposal?: { id: string; items?: unknown[] }
      error?: string
    }>(
      'provisioning_propose_for_connection',
      { organization_id: organizationID, resource_connection_id: connID },
      'provisioning_proposal_created',
      { kind: 'organization', id: organizationID, action: 'provisioning_propose_for_connection' },
    )
      .then((r) => {
        const p = r.provisioning_proposal
        if (p?.id && (p.items?.length ?? 0) > 0) {
          popSheet()
          pushNav({ kind: 'onboarding_review', proposalID: p.id })
        }
      })
      .catch(() => {})
  }, [step, connID, organizationID])
  // Guards submitCredentials against re-entry (a double-click while the first
  // submit is mid-flight) — the connection + secret writes are not idempotent.
  const submittingRef = useRef(false)
  const [confirmedSlug, setConfirmedSlug] = useState<string>('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hostById = useMemo(() => {
    const m = new Map<string, Host>()
    for (const h of hosts) m.set(h.host_id, h)
    return m
  }, [hosts])

  // Only installs on an online host are connectable: OAuth and secret
  // encryption both need the host's X25519 pubkey, which the daemon
  // enrolls at boot. Offline host → no usable pubkey → begin_oauth errors.
  const services = useMemo<Service[]>(() => {
    const bySlug = new Map<string, Service>()
    for (const pi of pluginInstalls) {
      if (!hostById.get(pi.host_id)?.online) continue
      let svc = bySlug.get(pi.plugin_slug)
      if (!svc) {
        svc = {
          slug: pi.plugin_slug,
          displayName: pi.display_name || pi.plugin_slug,
          authScheme: pi.auth_scheme,
          installs: [],
        }
        bySlug.set(pi.plugin_slug, svc)
      }
      svc.installs.push(pi)
    }
    return Array.from(bySlug.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    )
  }, [pluginInstalls, hostById])

  const pickedInstall = useMemo(
    () => pluginInstalls.find((p) => p.id === pickedInstallID) ?? null,
    [pluginInstalls, pickedInstallID],
  )
  const pickedService = useMemo(
    () => services.find((s) => s.slug === pickedServiceSlug) ?? null,
    [services, pickedServiceSlug],
  )
  const configSchema = useMemo(
    () => parseConfigSchema(pickedInstall?.config_schema),
    [pickedInstall],
  )
  const credSpecs = useMemo(
    () => parseCredentialSpecs(pickedInstall?.credential_specs),
    [pickedInstall],
  )
  const secretCreds = useMemo(() => credSpecs.filter((c) => c.secret), [credSpecs])
  // Provider slug from the manifest's `type: oauth2_<provider>`, not hardcoded.
  const oauthProvider = useMemo(() => oauthProviderFromSpecs(credSpecs), [credSpecs])
  const isOAuth = pickedInstall?.auth_scheme === 'oauth2_user'
  const hostID = pickedInstall?.host_id ?? ''
  const hostSecrets = useMemo(
    () => allSecrets.filter((s) => s.host_id === hostID),
    [allSecrets, hostID],
  )
  const hasConfig =
    !!configSchema?.properties && Object.keys(configSchema.properties).length > 0

  // ---- navigation ----

  function pickService(svc: Service) {
    setError(null)
    setPickedServiceSlug(svc.slug)
    if (svc.installs.length === 1) {
      selectInstall(svc.installs[0])
    } else {
      setStep('host')
    }
  }

  function selectInstall(pi: PluginInstall) {
    // Changing the target install invalidates any connection we already
    // created for a previous pick — drop it so ensureConnection() mints a
    // fresh one against the right host rather than reusing a stale id.
    if (pi.id !== pickedInstallID) {
      setConnID(null)
      setConfirmedSlug('')
    }
    setPickedInstallID(pi.id)
    if (!connectionSlug) setConnectionSlug(slugify(pi.plugin_slug))
    setStep('details')
  }

  function handleDetailsNext(e: React.FormEvent) {
    e.preventDefault()
    if (!connectionSlug.trim()) return
    setError(null)
    if (hasConfig) {
      setStep('config')
    } else {
      afterConfig()
    }
  }

  function handleConfigNext(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    afterConfig()
  }

  // Plaintext-trust gate (secret-exposure-model.md): a credential is sealed to
  // the picked host and is decryptable there, so if that host's owner isn't the
  // signed-in user, they can read the value. Confirm before proceeding down
  // EITHER credential path. Own host (or unknown owner) → no prompt. This is the
  // grantor protecting themselves from their own footgun (begin_oauth only
  // checks org membership, not host ownership), so a client gate is the right
  // level. Returns true to proceed.
  function confirmForeignHost(): boolean {
    const warning = foreignHostWarning(hostById.get(hostID), humanUserID)
    return !warning || confirm(warning)
  }

  // Fork on auth scheme. OAuth needs the connection created up front (so
  // begin_oauth has an id); the credential path defers creation to submit.
  async function afterConfig() {
    if (!confirmForeignHost()) return
    if (isOAuth) {
      const ok = await ensureConnection()
      if (ok) setStep('auth')
    } else {
      setCredIndex(0)
      setStep('cred')
    }
  }

  // Creates the connection once and RETURNS its id (or null on failure). Safe to
  // call repeatedly (retry after a transient failure reuses the existing row).
  // It returns the id rather than relying on the connID state, because callers
  // use the connection immediately — and setConnID() is an async React update that
  // has NOT landed by the next line. (Reading the stale connID here is the bug that
  // created a connection but never filed its credential.)
  async function ensureConnection(): Promise<string | null> {
    if (connID) return connID
    if (!pickedInstall) {
      setError('No plugin selected')
      return null
    }
    setBusy(true)
    setError(null)
    try {
      const resp = await createResourceConnection({
        slug: connectionSlug.trim(),
        pluginInstallID: pickedInstall.id,
        displayName: displayName.trim() || undefined,
        config: buildConfigJSON(configSchema, configValues),
      })
      if (resp?.error) {
        setError(resp.error)
        return null
      }
      setConnID(resp.id!)
      setConfirmedSlug(resp.slug ?? connectionSlug.trim())
      return resp.id!
    } catch (e) {
      setError(String(e))
      return null
    } finally {
      setBusy(false)
    }
  }

  function handleCredNext(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (credIndex < secretCreds.length - 1) {
      setCredIndex((i) => i + 1)
    } else {
      submitCredentials()
    }
  }

  // Non-OAuth finish: create the connection, then for each secret
  // credential either reuse a picked secret or encrypt + store a new one,
  // and file it on the connection under the manifest's credential name.
  //
  // Filing by name is why a plugin declaring two credentials now works.
  // The old flow minted one identity row per credential and marked only
  // the first one default, so the second was unreachable.
  async function submitCredentials() {
    if (submittingRef.current) return
    submittingRef.current = true
    try {
      // Use the id ensureConnection returns — NOT the connID state, which hasn't
      // updated yet on this line (the bug that left connections credential-less).
      const cid = await ensureConnection()
      if (!cid) return
      setBusy(true)
      setError(null)

      const needsNewSecret = secretCreds.some(
        (_, i) => !credSecretIDs.has(i) && !!credValues.get(i),
      )
      let rawPub: Uint8Array | null = null
      if (needsNewSecret) {
        const pubResp = await fetchHostPubkey(hostID)
        if (pubResp.error || !pubResp.pubkey) {
          setError(
            pubResp.error ??
              "This host has no encryption key yet. Bring its daemon online, then try again.",
          )
          return
        }
        rawPub = base64Decode(pubResp.pubkey)
      }

      for (let i = 0; i < secretCreds.length; i++) {
        const spec = secretCreds[i]
        let secretID: string | null = credSecretIDs.get(i) ?? null

        if (!secretID) {
          const plaintext = credValues.get(i)
          if (!plaintext) continue
          const secretName = `${confirmedSlug}.${spec.name}`
          const envelope = encryptToHostPubkey(rawPub!, new TextEncoder().encode(plaintext))
          const putResp = await putSecret({
            hostID,
            name: secretName,
            ciphertextB64: base64Encode(envelope),
            purpose: `${spec.name} for ${cid}`,
          })
          if (putResp.error) {
            setError(`Secret ${spec.name}: ${putResp.error}`)
            return
          }
          secretID = putResp.id!
        }

        const credResp = await setConnectionCredential({
          resourceConnectionID: cid,
          secretID,
          name: spec.name,
        })
        if (credResp.error) {
          setError(`Credential ${spec.name}: ${credResp.error}`)
          return
        }
      }
      fetchResourceConnections()
      setStep('done')
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
      submittingRef.current = false
    }
  }

  function back() {
    setError(null)
    switch (step) {
      case 'service':
        popSheet()
        break
      case 'host':
        setStep('service')
        break
      case 'details':
        setStep(pickedService && pickedService.installs.length > 1 ? 'host' : 'service')
        break
      case 'config':
        setStep('details')
        break
      case 'cred':
        if (credIndex > 0) setCredIndex((i) => i - 1)
        else setStep(hasConfig ? 'config' : 'details')
        break
      case 'auth':
        // The connection already exists; stepping back to config is fine,
        // ensureConnection() will reuse it.
        setStep(hasConfig ? 'config' : 'details')
        break
      case 'done':
        popSheet()
        break
    }
  }

  const title = titleFor(step, pickedService?.displayName)

  return (
    <div className="wizard wizard--sheet">
      <header className="wizard__nav">
        <button className="wizard__nav-btn" onClick={back} disabled={busy}>
          {step === 'service' || step === 'done' ? 'Cancel' : '‹ Back'}
        </button>
        <div className="wizard__nav-title">{title}</div>
        <div className="wizard__nav-spacer" />
      </header>

      <div className="wizard__body">
        <div className="wizard__step">
          {error && (
            <div className="wizard__error" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}

          {/* Auth-scheme explainer once a service is chosen */}
          {pickedInstall?.auth_scheme && step !== 'service' && step !== 'host' && step !== 'done' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <AuthSchemeBadge scheme={pickedInstall.auth_scheme} />
              <span style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.4 }}>
                {authSchemeExplainer(pickedInstall.auth_scheme)}
              </span>
            </div>
          )}

          {step === 'service' && (
            <ServiceStep services={services} onPick={pickService} />
          )}

          {step === 'host' && pickedService && (
            <HostStep
              service={pickedService}
              hostById={hostById}
              onPick={selectInstall}
            />
          )}

          {step === 'details' && (
            <DetailsStep
              connectionSlug={connectionSlug}
              displayName={displayName}
              onChangeSlug={setConnectionSlug}
              onChangeName={setDisplayName}
              onNext={handleDetailsNext}
              disabled={busy}
            />
          )}

          {step === 'config' && configSchema && (
            <ConfigStep
              schema={configSchema}
              values={configValues}
              onChange={setConfigValues}
              onNext={handleConfigNext}
              disabled={busy}
            />
          )}

          {step === 'cred' && secretCreds[credIndex] && (
            <CredentialStep
              spec={secretCreds[credIndex]}
              credIndex={credIndex}
              totalSecretCreds={secretCreds.length}
              value={credValues.get(credIndex) ?? ''}
              onChange={(v) => {
                setCredValues((m) => new Map(m).set(credIndex, v))
                setCredSecretIDs((m) => { const n = new Map(m); n.delete(credIndex); return n })
              }}
              existingSecrets={hostSecrets}
              pickedSecretID={credSecretIDs.get(credIndex) ?? null}
              onPickSecret={(id) => {
                setCredSecretIDs((m) => new Map(m).set(credIndex, id))
                setCredValues((m) => { const n = new Map(m); n.delete(credIndex); return n })
              }}
              onNext={handleCredNext}
              disabled={busy}
              nextLabel={credIndex < secretCreds.length - 1 ? 'Continue' : 'Connect'}
            />
          )}

          {step === 'auth' && connID && pickedInstall && (
            <OAuthConnectStep
              connectionID={connID}
              hostID={hostID}
              provider={oauthProvider}
              pluginDisplayName={pickedInstall.display_name ?? pickedInstall.plugin_slug}
              onConnected={() => {
                fetchResourceConnections()
                setStep('done')
              }}
              onError={setError}
            />
          )}

          {step === 'done' && (
            <DoneStep
              serviceName={pickedInstall?.display_name ?? pickedService?.displayName ?? 'account'}
              onClose={popSheet}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function ServiceStep({
  services,
  onPick,
}: {
  services: Service[]
  onPick: (s: Service) => void
}) {
  if (services.length === 0) {
    return (
      <div style={{ padding: '8px 0' }}>
        <p style={{ fontSize: 14, opacity: 0.7, lineHeight: 1.5 }}>
          No connectable services yet. A service shows up here once a host is
          online with its plugin installed.
        </p>
        <a
          href={getSetupGuideURL()}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 14, color: 'var(--tint)' }}
        >
          Setup guide →
        </a>
      </div>
    )
  }
  return (
    <ul className="wizard__list">
      {services.map((svc) => (
        <li key={svc.slug}>
          <button className="wizard__row" onClick={() => onPick(svc)}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <span>
                {svc.displayName}
                {svc.authScheme && <AuthSchemeBadge scheme={svc.authScheme} />}
              </span>
              <span className="wizard__row-sub">
                {svc.installs.length > 1
                  ? `${svc.installs.length} hosts`
                  : svc.slug}
              </span>
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}

function HostStep({
  service,
  hostById,
  onPick,
}: {
  service: Service
  hostById: Map<string, Host>
  onPick: (pi: PluginInstall) => void
}) {
  return (
    <div>
      <p style={{ fontSize: 13, opacity: 0.7, marginTop: 0 }}>
        {service.displayName} is installed on more than one host. Which one
        should this connection use?
      </p>
      <ul className="wizard__list">
        {service.installs.map((pi) => {
          const h = hostById.get(pi.host_id)
          return (
            <li key={pi.id}>
              <button className="wizard__row" onClick={() => onPick(pi)}>
                <span>{h?.hostname ?? pi.host_id}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function DoneStep({
  serviceName,
  onClose,
}: {
  serviceName: string
  onClose: () => void
}) {
  return (
    <div style={{ textAlign: 'center', padding: '24px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 40 }}>✓</div>
      <div style={{ fontSize: 17, fontWeight: 600 }}>{serviceName} connected</div>
      <p style={{ fontSize: 14, opacity: 0.75, lineHeight: 1.5, margin: 0 }}>
        Your household now has this account. To let an agent use it, open the
        agent, go to its Resources tab, and grant access.
      </p>
      <button className="wizard__primary" onClick={onClose}>
        Done
      </button>
    </div>
  )
}

function titleFor(step: Step, serviceName?: string): string {
  switch (step) {
    case 'service':
      return 'Connect an account'
    case 'host':
      return 'Choose host'
    case 'details':
      return serviceName ? `Name your ${serviceName}` : 'Name connection'
    case 'config':
      return 'Configure'
    case 'cred':
      return 'Credentials'
    case 'auth':
      return 'Sign in'
    case 'done':
      return 'Connected'
  }
}
